import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Code, ConnectError } from '@connectrpc/connect'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser } from '@fortawesome/free-solid-svg-icons'

import ConnectionCard from '@components/connection-card/ConnectionCard'
import LabelAndTextInput from '@components/inputs/LabelAndTextInput'
import Button from '@components/buttons/Button'
import padlock from '@assets/form/padlock.svg'
import { emailValidator, useValidatorContext } from '@/utils'
import { useBrokers } from '@/providers/brokerProvider'
import { useSessionTokenCookie, useSessionTokenVerification } from '@/hooks/useSessionToken'

type Feedback = { text: string; kind: 'error' | 'notice' }

const noFeedback: Feedback = { text: '', kind: 'error' }

// passwordResetError turns the back-end's refusal into something the broker can
// act on: whether to wait, or to go and find an administrator.
function passwordResetError(error: unknown): string {
	switch (ConnectError.from(error).code) {
		case Code.Unavailable:
			// No SMTP server is configured, so no link can be sent at all.
			return "La réinitialisation par email n'est pas disponible pour le moment. Contactez votre administrateur pour réinitialiser votre mot de passe."
		case Code.ResourceExhausted:
			return 'Trop de demandes pour cette adresse. Réessayez dans une heure.'
		default:
			return "Une erreur est survenue lors de l'envoi de l'email. Réessayez dans quelques instants."
	}
}

interface FormAdminConnectionProps {
	/** Which form the card shows. Both modes have their own route. */
	mode?: 'login' | 'password-reset'
}

// FormAdminConnection is the signed-out card: the page chrome never changes,
// only the form inside it (log in / ask for a password reset).
function FormAdminConnection({ mode = 'login' }: FormAdminConnectionProps) {
	const isPasswordReset = mode === 'password-reset'
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [feedback, setFeedback] = useState<Feedback>(noFeedback)
	const [submitting, setSubmitting] = useState(false)
	const brokers = useBrokers()
	const validator = useValidatorContext()
	const navigate = useNavigate()
	const formRef = useRef<HTMLFormElement>(null)
	useSessionTokenVerification({
		mode: 'redirect-if-present',
		redirectTo: '/form-admin',
	})
	const { setSessionToken } = useSessionTokenCookie()

	const handleEmailChange = (value: string) => {
		setEmail(value)
	}

	const handlePasswordChange = (value: string) => {
		setPassword(value)
	}

	// Submit through the form so the browser sees a real submit event and offers
	// to save / autofill the credentials.
	const submitForm = () => formRef.current?.requestSubmit()

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			event.preventDefault()
			submitForm()
		}
	}

	const handleLogin = async () => {
		try {
			const sendResponse = await brokers.client.login({
				email: email,
				password: password,
			})

			// Set cookie with session token
			setSessionToken(sendResponse.token)

			// Redirect to admin dashboard
			window.location.href = '/form-admin'
		} catch (error) {
			console.error('Login error: ', error)
			setFeedback({ text: 'Email ou mot de passe incorrect.', kind: 'error' })
			return
		}
	}

	const handlePasswordReset = async () => {
		if (!validator.validate()) return

		setSubmitting(true)
		try {
			await brokers.client.requestPasswordReset({ email })
			// Worded so it says nothing about whether the address has an account:
			// the back-end answers the same either way on purpose.
			setFeedback({
				text: 'Si un compte existe pour cette adresse, un email contenant un lien de réinitialisation vient de lui être envoyé.',
				kind: 'notice',
			})
		} catch (error) {
			console.error('Password reset error: ', error)
			setFeedback({ text: passwordResetError(error), kind: 'error' })
		} finally {
			setSubmitting(false)
		}
	}

	const handleSubmit = () => {
		setFeedback(noFeedback)

		if (isPasswordReset) {
			handlePasswordReset()
			return
		}
		handleLogin()
	}

	return (
		<ConnectionCard>
			<form
				ref={formRef}
				autoComplete='on'
				onKeyDown={handleKeyDown}
				onSubmit={event => {
					event.preventDefault()
					handleSubmit()
				}}
			>
				{isPasswordReset && (
					<p className='connectionIntro'>
						Renseignez votre email pour recevoir un lien de réinitialisation.
					</p>
				)}
				<LabelAndTextInput
					id='email'
					name='email'
					type={isPasswordReset ? 'email' : 'text'}
					label='Email'
					className='connectionField'
					hideLabel={true}
					icon={<FontAwesomeIcon icon={faUser} />}
					autocomplete='username'
					placeholder='Email'
					value={email}
					onChange={handleEmailChange}
					validator={isPasswordReset ? (input: string) => [emailValidator(input)] : undefined}
				/>
				{!isPasswordReset && (
					<LabelAndTextInput
						id='password'
						name='password'
						type='password'
						label='Mot de passe'
						className='connectionField'
						hideLabel={true}
						icon={<img src={padlock} alt='' />}
						autocomplete='current-password'
						placeholder='Mot de passe'
						value={password}
						onChange={handlePasswordChange}
					/>
				)}
				<Button id='send' onClick={submitForm} loading={submitting}>
					{isPasswordReset ? 'Envoyer' : 'Se connecter'}
				</Button>
				{feedback.text !== '' && (
					<p className={feedback.kind === 'error' ? 'connectionError' : 'connectionNotice'}>
						{feedback.text}
					</p>
				)}
				<div className='connectionFooter'>
					{isPasswordReset ? (
						<button
							type='button'
							className='connectionFooterLink'
							onClick={() => navigate('/form-admin-connection')}
						>
							Retour à la connexion
						</button>
					) : (
						<button
							type='button'
							className='connectionFooterLink'
							onClick={() => navigate('/form-admin-password-reset')}
						>
							Mot de passe oublié
						</button>
					)}
				</div>
			</form>
		</ConnectionCard>
	)
}

export default FormAdminConnection
